import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useLocation, Link } from "react-router-dom";
import { ShieldStar, SignOut, Buildings, EnvelopeOpen, ChartLineUp, ChatCircleDots, CurrencyGbp, BookOpen, GraduationCap, Megaphone, RocketLaunch, Bell, CheckCircle } from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: ChartLineUp, exact: true },
  { to: "/admin/accounts", label: "Accounts", icon: Buildings },
  { to: "/admin/leads", label: "Leads", icon: EnvelopeOpen },
  { to: "/admin/assistant", label: "Assistant", icon: ChatCircleDots },
  { to: "/admin/pricing", label: "Pricing", icon: CurrencyGbp },
  { to: "/admin/guides", label: "Guides", icon: BookOpen },
  { to: "/admin/learning", label: "Learning", icon: GraduationCap },
  { to: "/admin/social", label: "Social", icon: Megaphone },
  { to: "/admin/launch", label: "Launch", icon: RocketLaunch },
];

const fmtWhen = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function NotificationBell() {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const load = () => api.get("/admin/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/admin/notifications/read", {}).catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="admin-notif-bell"
        className="relative w-10 h-10 rounded-xl flex items-center justify-center bg-white border border-slate-200 hover:border-navy/30 transition-colors">
        <Bell size={18} weight={data.unread ? "fill" : "regular"} className="text-navy" />
        {data.unread > 0 && (
          <span data-testid="admin-notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E5484D] text-white text-[0.62rem] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="admin-notif-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto bg-white rounded-2xl shadow-xl border border-slate-100 z-50">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-navy">Notifications</div>
          {(data.notifications || []).length === 0 ? (
            <div className="p-8 text-center text-navy/55 text-sm" data-testid="admin-notif-empty">You're all caught up 🎉</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {data.notifications.map((n) => (
                <div key={n.id} className={`px-4 py-3 flex items-start gap-3 ${n.read ? "" : "bg-[#00A859]/[0.04]"}`} data-testid={`admin-notif-${n.id}`}>
                  <CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" style={{ color: "#00A859" }} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy">{n.title}</div>
                    <div className="text-xs text-navy/70">{n.body}</div>
                    <div className="text-[0.65rem] text-navy/45 mt-0.5">{fmtWhen(n.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();

  return (
    <div className="min-h-screen flex" style={{ background: "#f4f5f9" }}>
      <aside className="w-64 shrink-0 flex flex-col text-white" style={{ background: "#0E0F3F" }}>
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(244,122,36,0.18)" }}>
            <ShieldStar size={20} weight="fill" style={{ color: "#F37A24" }} />
          </span>
          <div>
            <div className="font-heading font-bold leading-tight">WorkWithLottie</div>
            <div className="text-[0.6rem] uppercase tracking-[0.16em] text-[#F37A24] font-semibold">Back office</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${active ? "bg-white/10 text-white font-medium" : "text-white/65 hover:bg-white/5 hover:text-white"}`}>
                <Icon size={18} weight={active ? "fill" : "regular"} /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-xs text-white/70 mb-2 truncate">{user?.email}</div>
          <button onClick={logout} data-testid="admin-logout"
            className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="flex justify-end px-8 pt-5 max-w-[1280px] mx-auto">
          <NotificationBell />
        </div>
        <div className="px-8 pb-8 pt-3 max-w-[1280px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
